import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from "../../../components/ui/select";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../App";
import {
  Refilling,
  RefillingFormData,
  STATE_OPTIONS,
  VISIT_PERIOD_OPTIONS,
  QUANTITY_FIELDS,
  TEXT_FIELDS,
} from "../types/refilling";
import { LocationCodeFields } from "../../../components/panel/LocationCodeFields";
import { useSiteLocationOptions } from "../../../hooks/useSiteLocationOptions";
import { normalizeStateToFormValue, stateMatches } from "../../../utils/state";
import {
  Plus,
  Search,
  Trash2,
  Edit2,
  Calendar,
  MapPin,
  AlertCircle,
  X,
  Loader2,
  Upload,
  Image as ImageIcon,
} from "lucide-react";

export const RefillingPage = () => {
  const [refillingRecords, setRefillingRecords] = useState<Refilling[]>([]);
  const [allRefillingRecords, setAllRefillingRecords] = useState<Refilling[]>([]);
  const [totalRefilling, setTotalRefilling] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    refillingId: string;
    dcNo: string;
  } | null>(null);
  const [editingRefillingId, setEditingRefillingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);
  const [locationCodeSelectOpen, setLocationCodeSelectOpen] = useState(false);
  const [oldLocationCodeSelectOpen, setOldLocationCodeSelectOpen] = useState(false);
  const [createdByName, setCreatedByName] = useState<string>("");
  const [createdById, setCreatedById] = useState<string>("");
  const [usedVisitPeriods, setUsedVisitPeriods] = useState<string[]>([]);

  const authToken = useSelector((state: any) => state.loginData?.authToken);
  const id = useSelector((state: any) => state.loginData?.id);
  const userRole = useSelector((state: any) => state.loginData?.role);
  const userName = useSelector((state: any) => state.loginData?.name);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const todayDate = getTodayDate();

  const [formData, setFormData] = useState<RefillingFormData>({
    state: "",
    locationCode: "",
    oldLocationCode: "",
    visitPeriod: "",
    visitDate: todayDate,
    dcNo: "",
    abc_2_0_kg: "",
    abc_4_0_kg: "",
    abc_6_0_kg: "",
    abc_9_0_kg: "",
    co2_2_0_kg: "",
    co2_4_5_kg: "",
    co2_9_0_kg: "",
    foam_9_0_lrs: "",
    clean_agent_4_0_kg: "",
    clean_agent_10_0_kg: "",
    safety_pin: "",
    pressure_gauge: "",
    abc_hose: "",
    co2_hose: "",
    co2_valve: "",
    abc_valve: "",
    additional_field_1: "",
    additional_field_2: "",
    refillingDoc: "",
  });

  const {
    loadingSites,
    newLocationCodeOptions,
    oldLocationCodeOptions,
    findSiteByLocationCode,
    findSiteByOldLocationCode,
  } = useSiteLocationOptions(authToken || "", String(id || ""), formData.state);

  // Fetch all Refilling records for search
  const fetchAllRefillingForSearch = async () => {
    if (!authToken || !id) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/refilling?page=1&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setAllRefillingRecords(response.data.refillingRecords || []);
      setTotalRefilling(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching refilling records:", error);
      alert("Failed to fetch refilling records");
    } finally {
      setLoading(false);
    }
  };

  // Get paginated records
  const getPaginatedRecords = (recordsToDisplay: Refilling[]) => {
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    return recordsToDisplay.slice(startIndex, endIndex);
  };

  // Fetch all refilling records for visit period filtering (no pagination)
  const fetchAllRefillingRecords = async (): Promise<Refilling[]> => {
    if (!authToken || !id) return [];

    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/refilling?page=1&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data.refillingRecords || [];
    } catch (error) {
      console.error("Error fetching all refilling records:", error);
      return [];
    }
  };

  // Fetch used visit periods for a location
  const fetchUsedVisitPeriods = async (locationCode: string, state: string) => {
    if (!locationCode) {
      setUsedVisitPeriods([]);
      return;
    }

    try {
      // Fetch all refilling records to get accurate used visit periods
      const allRefilling = await fetchAllRefillingRecords();
      
      const usedPeriods: string[] = [];
      allRefilling.forEach((refilling) => {
        if (
          refilling.locationCode.toLowerCase() === locationCode.toLowerCase() &&
          stateMatches(refilling.state, state)
        ) {
          usedPeriods.push(refilling.visitPeriod);
        }
      });
      setUsedVisitPeriods(usedPeriods);
    } catch (error) {
      console.error("Error fetching used visit periods:", error);
      setUsedVisitPeriods([]);
    }
  };

  useEffect(() => {
    fetchAllRefillingForSearch();
  }, [authToken, id]);

  useEffect(() => {
    if (!isDrawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Handle state change
  const handleStateChange = (value: string) => {
    setFormData({
      ...formData,
      state: value,
      locationCode: "",
      oldLocationCode: "",
      visitPeriod: "",
    });
    setUsedVisitPeriods([]);
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
  };

  const handleLocationCodeChange = (value: string) => {
    const selectedSite = findSiteByLocationCode(value);
    const oldCode = selectedSite?.oldLocationCode || "";

    setFormData({
      ...formData,
      locationCode: value,
      oldLocationCode: oldCode,
      visitPeriod: "",
    });
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
    fetchUsedVisitPeriods(value, formData.state);
  };

  const handleOldLocationCodeChange = (value: string) => {
    const selectedSite = findSiteByOldLocationCode(value);
    const locCode = selectedSite?.locationCode || "";

    setFormData({
      ...formData,
      locationCode: locCode,
      oldLocationCode: value,
      visitPeriod: "",
    });
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
    if (locCode) {
      fetchUsedVisitPeriods(locCode, formData.state);
    }
  };

  // Handle visit date change
  const handleVisitDateChange = (value: string) => {
    setFormData({
      ...formData,
      visitDate: value,
    });
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.locationCode.trim())
      newErrors.locationCode = "New location code is required";
    if (!formData.oldLocationCode.trim())
      newErrors.oldLocationCode = "Old location code is required";
    if (!formData.visitPeriod.trim())
      newErrors.visitPeriod = "Visit period is required";
    if (!formData.visitDate.trim())
      newErrors.visitDate = "Visit date is required";
    if (!formData.dcNo.trim()) newErrors.dcNo = "DC No is required";
    if (!formData.refillingDoc || !formData.refillingDoc.trim())
      newErrors.refillingDoc = "Delivery Challan (DC) Image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle image upload
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to Google Drive
    setUploadingImage(true);
    try {
      // Rename file to location code
      const fileExtension = file.name.split('.').pop();
      const newFileName = `${formData.locationCode}.${fileExtension}`;
      const renamedFile = new File([file], newFileName, { type: file.type });

      const uploadFormData = new FormData();
      uploadFormData.append("file", renamedFile);
      uploadFormData.append("visitDate", formData.visitDate);

      const response = await axios.post(
        `${API_BASE_URL}account/${id}/refilling/upload-image`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.driveLink) {
        setFormData({
          ...formData,
          refillingDoc: response.data.driveLink,
        });
        alert(`Image uploaded successfully!\nLink: ${response.data.driveLink}`);
      } else {
        alert("Image uploaded but no link received.");
      }
    } catch (error: any) {
      console.error("Error uploading image:", error);
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to upload image";
      alert(`Error: ${errorMsg}`);
      setPreviewImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  // Create/Update Refilling
  const handleCreateRefilling = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload: any = {
        state: formData.state,
        locationCode: formData.locationCode,
        oldLocationCode: formData.oldLocationCode,
        visitPeriod: formData.visitPeriod,
        visitDate: formData.visitDate,
        dcNo: formData.dcNo,
      };

      // Add quantity fields - convert empty string to 0
      QUANTITY_FIELDS.forEach((field) => {
        const value = formData[field.key as keyof RefillingFormData];
        const numValue = value === "" || value === 0 ? 0 : parseInt(String(value)) || 0;
        payload[field.key] = numValue;
      });

      // Add text fields - keep as strings
      TEXT_FIELDS.forEach((field) => {
        const value = formData[field.key as keyof RefillingFormData];
        payload[field.key] = String(value || "");
      });

      // Add document if it has a value
      if (formData.refillingDoc && formData.refillingDoc.trim()) {
        payload.refilling_doc = formData.refillingDoc;
      }

      if (editingRefillingId) {
        // Update (admin only)
        await axios.put(
          `${API_BASE_URL}account/${id}/refilling/${editingRefillingId}/update`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        setSuccessMessage("Refilling record updated successfully!");
      } else {
        // Create
        await axios.post(
          `${API_BASE_URL}account/${id}/refilling/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        setSuccessMessage("Refilling record created successfully!");
      }

      setTimeout(() => setSuccessMessage(""), 3000);
      closeDrawer();
      fetchAllRefillingForSearch();
    } catch (error: any) {
      console.error("Error creating/updating refilling:", error);
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to save refilling";
      if (error.response?.status === 409) {
        setErrors({
          locationCode: errorMsg,
          visitPeriod: errorMsg,
        });
      }
      alert(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    setLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/refilling/${deleteConfirm.refillingId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setSuccessMessage("Refilling record deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchAllRefillingForSearch();
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error("Error deleting refilling:", error);
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to delete refilling";
      alert(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (refilling: Refilling) => {
    setEditingRefillingId(refilling.refillingId);
    setCreatedById(refilling.createdBy);
    setCreatedByName(refilling.createdByName || refilling.createdBy || "Unknown");

    const updatedFormData: RefillingFormData = {
      state: normalizeStateToFormValue(refilling.state),
      locationCode: refilling.locationCode,
      oldLocationCode: refilling.oldLocationCode,
      visitPeriod: refilling.visitPeriod,
      visitDate: refilling.visitDate,
      dcNo: refilling.dcNo,
      abc_2_0_kg: refilling.abc_2_0_kg ? String(refilling.abc_2_0_kg) : "",
      abc_4_0_kg: refilling.abc_4_0_kg ? String(refilling.abc_4_0_kg) : "",
      abc_6_0_kg: refilling.abc_6_0_kg ? String(refilling.abc_6_0_kg) : "",
      abc_9_0_kg: refilling.abc_9_0_kg ? String(refilling.abc_9_0_kg) : "",
      co2_2_0_kg: refilling.co2_2_0_kg ? String(refilling.co2_2_0_kg) : "",
      co2_4_5_kg: refilling.co2_4_5_kg ? String(refilling.co2_4_5_kg) : "",
      co2_9_0_kg: refilling.co2_9_0_kg ? String(refilling.co2_9_0_kg) : "",
      foam_9_0_lrs: refilling.foam_9_0_lrs ? String(refilling.foam_9_0_lrs) : "",
      clean_agent_4_0_kg: refilling.clean_agent_4_0_kg ? String(refilling.clean_agent_4_0_kg) : "",
      clean_agent_10_0_kg: refilling.clean_agent_10_0_kg ? String(refilling.clean_agent_10_0_kg) : "",
      safety_pin: refilling.safety_pin ? String(refilling.safety_pin) : "",
      pressure_gauge: refilling.pressure_gauge ? String(refilling.pressure_gauge) : "",
      abc_hose: refilling.abc_hose ? String(refilling.abc_hose) : "",
      co2_hose: refilling.co2_hose ? String(refilling.co2_hose) : "",
      co2_valve: refilling.co2_valve ? String(refilling.co2_valve) : "",
      abc_valve: refilling.abc_valve ? String(refilling.abc_valve) : "",
      additional_field_1: refilling.additional_field_1 || "",
      additional_field_2: refilling.additional_field_2 || "",
      refillingDoc: refilling.refillingDoc || "",
    };

    setFormData(updatedFormData);
    // Fetch used periods (excluding current record)
    const usedPeriods = allRefillingRecords
      .filter(
        (record) =>
          record.locationCode.toLowerCase() === refilling.locationCode.toLowerCase() &&
          stateMatches(record.state, refilling.state) &&
          record.refillingId !== refilling.refillingId
      )
      .map((record) => record.visitPeriod);
    setUsedVisitPeriods(usedPeriods);
    // Set preview image if available (Google Drive link)
    if (refilling.refillingDoc && refilling.refillingDoc.trim()) {
      setPreviewImage(refilling.refillingDoc);
    } else {
      setPreviewImage(null);
    }
    setIsDrawerOpen(true);
  };

  // Close drawer
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingRefillingId(null);
    setCreatedByName("");
    setCreatedById("");
    setFormData({
      state: "",
      locationCode: "",
      oldLocationCode: "",
      visitPeriod: "",
      visitDate: todayDate,
      dcNo: "",
      abc_2_0_kg: "",
      abc_4_0_kg: "",
      abc_6_0_kg: "",
      abc_9_0_kg: "",
      co2_2_0_kg: "",
      co2_4_5_kg: "",
      co2_9_0_kg: "",
      foam_9_0_lrs: "",
      clean_agent_4_0_kg: "",
      clean_agent_10_0_kg: "",
      safety_pin: "",
      pressure_gauge: "",
      abc_hose: "",
      co2_hose: "",
      co2_valve: "",
      abc_valve: "",
      additional_field_1: "",
      additional_field_2: "",
      refillingDoc: "",
    });
    setPreviewImage(null);
    setErrors({});
    setUsedVisitPeriods([]);
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
  };

  // Filter and sort refilling records by creation date (newest first) - search across all records
  const filteredRefilling = getPaginatedRecords(
    allRefillingRecords
      .filter((refilling) => {
        const searchMatch =
          refilling.dcNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          refilling.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
          refilling.locationCode.toLowerCase().includes(searchTerm.toLowerCase());
        // No search: own records only. Search active: all matching records (own + others).
        const userMatch = userRole === "admin" || searchTerm !== "" || String(refilling.createdBy) === String(id);
        return searchMatch && userMatch;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  );

  // Get total filtered count for pagination
  const filteredTotalCount = allRefillingRecords.filter((refilling) => {
    const searchMatch =
      refilling.dcNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refilling.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refilling.locationCode.toLowerCase().includes(searchTerm.toLowerCase());
    // No search: own records only. Search active: all matching records (own + others).
    const userMatch = userRole === "admin" || searchTerm !== "" || String(refilling.createdBy) === String(id);
    return searchMatch && userMatch;
  }).length;

  // Check if user can edit this record (only admin can edit)
  const canEdit = (refilling: Refilling): boolean => {
    return userRole === "admin";
  };

  // Check if user can delete this record (both admin and staff can delete)
  const canDelete = (refilling: Refilling): boolean => {
    if (userRole === "admin") return true;
    return refilling.createdBy === String(id);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Refilling Management</h1>
          <p className="text-gray-600 mt-1">
            Manage fire extinguisher refilling records
          </p>
        </div>
        <Button
          onClick={() => {
            setIsDrawerOpen(true);
            setCreatedByName(userName || "Current User");
            setCreatedById(String(id || ""));
          }}
          className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white gap-2"
        >
          <Plus size={20} />
          Add New Refilling
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {successMessage}
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-3 text-gray-400" size={20} />
        <Input
          type="text"
          placeholder="Search by DC no, state, or location code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 py-2 border border-gray-300 rounded-lg w-full"
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="animate-spin text-cyan-500" size={40} />
        </div>
      )}

      {/* Refilling Records Grid */}
      {!loading && (
        <div className="space-y-4">
          {filteredRefilling.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">No refilling records found</p>
            </div>
          ) : (
            filteredRefilling.map((refilling) => (
              <div
                key={refilling.refillingId}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                  {/* Left Section - DC & Dates */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-800 truncate">
                     #{refilling.locationCode}
                    </h3>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p>
                        <span className="font-semibold">Visit Date:</span>{" "}
                        {new Date(refilling.visitDate).toLocaleDateString()}
                      </p>
                     
                    </div>
                  </div>

                  {/* Middle Section - Location Details */}
                  <div className="flex-1 min-w-0">
                    <div className="space-y-2 text-sm">
                      {refilling.refillingDoc && (
                        <div className="text-sm">
                          <button
                            onClick={() => {
                              setImageModalUrl(refilling.refillingDoc ?? null);
                              setImageModalOpen(true);
                            }}
                            className="text-blue-500 hover:underline flex items-center gap-1 cursor-pointer"
                          >
                            <ImageIcon size={14} />
                            View Image
                          </button>
                        </div>

                      )}
                       <p className="text-xs text-gray-500">
                        <span className="font-semibold">Created By:</span>{" "}
                        {refilling.createdByName || "Unknown"}
                      </p>
                    </div>
                  </div>

                  {/* Right Section - Actions */}
                  <div className="flex gap-2 w-full md:w-auto">
                    {canEdit(refilling) && (
                      <Button
                        onClick={() => handleEdit(refilling)}
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600 text-white gap-1 flex-1 md:flex-none"
                      >
                        <Edit2 size={16} />
                        View/Edit
                      </Button>
                    )}
                    {canDelete(refilling) && (
                      <Button
                        onClick={() =>
                          setDeleteConfirm({
                            refillingId: refilling.refillingId,
                            dcNo: refilling.dcNo,
                          })
                        }
                        size="sm"
                        className="bg-red-500 hover:bg-red-600 text-white gap-1 flex-1 md:flex-none"
                      >
                        <Trash2 size={16} />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Pagination */}
          <div className="flex justify-between items-center mt-6">
            <p className="text-sm text-gray-600">
              Showing {Math.max(1, (page - 1) * 10 + 1)} to {Math.min(page * 10, filteredTotalCount)} of {filteredTotalCount} records
            </p>
            <div className="space-x-2">
              <Button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                variant="outline"
              >
                Previous
              </Button>
              <Button
                onClick={() =>
                  setPage(
                    Math.min(Math.ceil(filteredTotalCount / 10), page + 1)
                  )
                }
                disabled={page >= Math.ceil(filteredTotalCount / 10)}
                variant="outline"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Delete Refilling Record
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the refilling record for DC #{deleteConfirm.dcNo}? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer/Modal for Create/Edit */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            data-refilling-drawer
            className="bg-white w-full md:w-96 max-h-[90vh] rounded-lg shadow-lg flex flex-col"
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white p-6 flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold">
                {editingRefillingId ? "Edit Refilling" : "Create New Refilling"}
              </h2>
              <button
                onClick={closeDrawer}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1"
              >
                <X size={24} />
              </button>
            </div>

            {/* Drawer Content - Scrollable */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* State */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  State *
                </Label>
                <Select
                  value={formData.state}
                  onValueChange={handleStateChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {STATE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.state && (
                  <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                )}
              </div>

              <LocationCodeFields
                state={formData.state}
                locationCode={formData.locationCode}
                oldLocationCode={formData.oldLocationCode}
                onLocationCodeChange={handleLocationCodeChange}
                onOldLocationCodeChange={handleOldLocationCodeChange}
                newLocationCodeOptions={newLocationCodeOptions}
                oldLocationCodeOptions={oldLocationCodeOptions}
                loadingSites={loadingSites}
                locationCodeOpen={locationCodeSelectOpen}
                onLocationCodeOpenChange={setLocationCodeSelectOpen}
                oldLocationCodeOpen={oldLocationCodeSelectOpen}
                onOldLocationCodeOpenChange={setOldLocationCodeSelectOpen}
                errors={{
                  locationCode: errors.locationCode,
                  oldLocationCode: errors.oldLocationCode,
                }}
              />
              {/* Visit Period */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Visit Period *
                </Label>
                <Select
                  value={formData.visitPeriod}
                  onValueChange={(value) =>
                    setFormData({ ...formData, visitPeriod: value })
                  }
                  disabled={!formData.locationCode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={!formData.locationCode ? "Select location first" : "Select period"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {VISIT_PERIOD_OPTIONS.filter(
                      (option) => !usedVisitPeriods.includes(option.value)
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!formData.locationCode && (
                  <p className="text-gray-500 text-sm mt-1">Please select a new location code first</p>
                )}
                {errors.visitPeriod && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.visitPeriod}
                  </p>
                )}
              </div>

              {/* Visit Date */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Visit Date *
                </Label>
                <Input
                  type="date"
                  value={formData.visitDate}
                  onChange={(e) => handleVisitDateChange(e.target.value)}
                  className="w-full"
                />
                {errors.visitDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.visitDate}
                  </p>
                )}
              </div>

              {/* DC No */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  DC No *
                </Label>
                <Input
                  type="text"
                  value={formData.dcNo}
                  onChange={(e) =>
                    setFormData({ ...formData, dcNo: e.target.value })
                  }
                  placeholder="Enter DC number"
                  className="w-full"
                />
                {errors.dcNo && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.dcNo}
                  </p>
                )}
              </div>

              {/* Created By - Display logged-in user (non-editable) */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Created By *
                </Label>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-gray-700 font-semibold">
                  {editingRefillingId ? (createdByName || "Unknown") : (userName && userName.trim() ? userName : "Current User")}
                </div>
                <p className="text-gray-500 text-xs mt-1">Automatically set to the user who created this record</p>
              </div>

              {/* Quantity Fields in 2-column layout */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-3">Quantities</h3>
                <div className="grid grid-cols-2 gap-3">
                  {QUANTITY_FIELDS.map((field) => (
                    <div key={field.key}>
                      <Label className="text-gray-700 text-sm mb-1 block">
                        {field.label}
                      </Label>
                      <Input
                        type="number"
                        value={
                          formData[field.key as keyof RefillingFormData] || ""
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            [field.key]: e.target.value,
                          })
                        }
                        placeholder="Enter quantity"
                        className="w-full"
                        min="0"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Text Fields */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-3">Additional Information</h3>
                <div className="space-y-3">
                  {TEXT_FIELDS.map((field) => (
                    <div key={field.key}>
                      <Label className="text-gray-700 text-sm mb-1 block">
                        {field.label}
                      </Label>
                      <Input
                        type="text"
                        value={
                          formData[field.key as keyof RefillingFormData] || ""
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            [field.key]: e.target.value,
                          })
                        }
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Delivery Challan (DC) Image *
                </Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {previewImage ? (
                    <div className="space-y-3">
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="max-h-40 mx-auto rounded-lg"
                      />
                      <label className="block">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                        <span className="text-blue-500 hover:underline cursor-pointer text-sm">
                          {uploadingImage ? "Uploading..." : "Change Image"}
                        </span>
                      </label>
                      {formData.refillingDoc && (
                        <a
                          href={formData.refillingDoc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline text-xs block"
                        >
                          View Uploaded Image
                        </a>
                      )}
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                      <div className="space-y-2">
                        <Upload
                          className="mx-auto text-gray-400"
                          size={32}
                        />
                        <p className="text-gray-600 text-sm">
                          {uploadingImage
                            ? "Uploading..."
                            : "Click to upload or drag and drop"}
                        </p>
                        <p className="text-gray-400 text-xs">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    </label>
                  )}
                </div>
                <p className="text-red-500 text-sm mt-1">Upload Only Refilling DC Copy</p>
                {errors.refillingDoc && (
                  <p className="text-red-500 text-sm mt-1">{errors.refillingDoc}</p>
                )}
              </div>
            </div>

            {/* Action Buttons - Fixed at Bottom */}
            <div className="flex gap-3 p-6 border-t border-gray-200 bg-white flex-shrink-0">
              <Button
                onClick={closeDrawer}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateRefilling}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : editingRefillingId ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {imageModalOpen && imageModalUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full">
            {/* Close Button */}
            <button
              onClick={() => {
                setImageModalOpen(false);
                setImageModalUrl(null);
              }}
              className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 z-10"
            >
              <X size={24} />
            </button>

            {/* Image Container */}
            <div className="p-4 flex items-center justify-center bg-gray-100 rounded-lg">
              <img
                src={imageModalUrl}
                alt="Document"
                className="max-w-full max-h-[70vh] object-contain rounded"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
              />
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-gray-200 flex gap-2">
              <a
                href={imageModalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded text-center"
              >
                Open in New Tab
              </a>
              <button
                onClick={() => {
                  setImageModalOpen(false);
                  setImageModalUrl(null);
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
